/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    object, string, func, bool,
} from 'prop-types';
import { useUIDSeed } from 'react-uid';
import Slider from 'react-slick';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIosTwoTone';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIosTwoTone';
import { useDispatch } from 'react-redux';
import { selectedSkuType, skuDataType, UpsellType } from '../common/types';
import GraphqlClubMonth from './GraphqlClubMonth';
import GraphqlClubUpgrade from './GraphqlClubUpgrade';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const styles = (theme) => ({
    mainContainer: {
        width: '85%',
        '@media only screen and (max-width: 1024px) ': {
            width: '100%',
        },
    },
    clubSelectionBox: {
        height: '115px',
        textAlign: 'center',
        display: 'inline-block',
        marginRight: 10,
        position: 'relative',
        boxShadow: '0px 0px 5px rgba(47, 47, 47, 0.25)',
        cursor: 'pointer',
        '&:hover': {
            boxShadow: '0px 0px 5px rgba(47, 47, 47, 0.25)',
        },
    },
    clubSelectionBoxSelected: {
        boxShadow: '0px 0px 0px 2px rgba(63, 141, 93, 1)',
        cursor: 'pointer',
        height: '115px',
        display: 'inline-block',
        position: 'relative',
        textAlign: 'center',
        marginRight: '10px',
        '&:focus-visible': {
            outline: 'none',
        },
    },
    relative: {
        position: 'relative',
    },
    clubTagline: {
        color: theme.palette.text?.primary || '#000',
        padding: '3px',
        fontWeight: '600',
        fontStyle: 'italic',
        fontSize: '0.75rem',
        textAlign: 'center',
        width: '100%',
        backgroundColor: theme.palette.cms?.white || '#ffffff',
        '&::before': {
            backgroundColor: theme.palette.cms?.black || '#000000',
            content: '',
            height: '1px',
            width: '50%',
        },
    },
    clubPrice: {
        margin: '7px 0',
        fontSize: '1rem',
        fontWeight: '700',
        marginTop: '36px',
        lineHeight: '1.25rem',
    },
    clubDuration: {
        margin: '10px 0',
        fontSize: '1rem',
        lineHeight: '1.25rem',
    },
    monthContainer: {
        display: 'flex',
    },

    forMonthClub: {
        '@media only screen and (max-width: 600px) ': {
            marginTop: '15px',
        },
    },

    clubUpsellTitle: {
        display: 'flex',
        gap: '16px',
        '@media only screen and (max-width: 1023.5px) ': {
            display: 'block',
            width: '100%',
        },
    },
    header: {
        fontSize: '1rem',
        lineHeight: '1.25rem',
        fontWeight: 700,
        margin: '18px 0px 12px 0px',
    },

    tagLineContainer: {
        width: '90%',
        textAlign: 'center',
        borderBottom: `1px solid ${theme.palette.cms?.black || '#2f2f2f'}`,
        lineHeight: ' 0.1em',
        margin: '10px 0px 20px',
        position: 'absolute',
        left: '5%',
    },
    subHeader: {
        fontSize: '0.875rem',
        lineHeight: '1.125rem',
        margin: '18px 0px 12px 0px',
        '@media only screen and (max-width: 1023.5px) ': {
            margin: '-10px 0px 6px 15px',
        },
    },
    clubCopy: {
        fontSize: '1rem',
        lineHeight: '1.5rem',
        display: 'block',
    },
    link: {
        textDecoration: 'underline',
        display: 'block',
        cursor: 'pointer',
    },
    carouselContainer: {
        position: 'relative',
        width: '100%',
        '@media only screen and (max-width: 600.5px) ': {
            paddingBottom: '0px',
        },
        '& .slick-disabled': {
            display: 'none !important',
        },
        '& .slick-list': {
            overflow: 'hidden',
            margin: '0 auto',
            width: '100%',
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
            },
            '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
                height: '3px',
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'transparent',
            },
        },
        '& .slick-slide': {
            display: 'inline-block',
            verticalAlign: 'top',
            padding: '6px',
        },
        '& .slick-next': {
            right: '2%',
            borderRadius: '50%',
            width: 30,
            background: theme.palette.headerColor || '#E8F1ED',
            transform: 'translate(0,-50%)',
            cursor: 'pointer',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '50%',
            height: 30,
            position: 'absolute',
            '@media only screen and (max-width: 600px) ': {
                display: 'none !important',
            },
            '& svg': {
                width: '0.6em',
                position: 'absolute',
                right: '18%',
            },
        },
        '& .slick-prev': {
            zIndex: '1',
            left: '2%',
            border: `1px solid ${theme.palette.headerColor || '#E8F1ED'}`,
            borderRadius: '50%',
            width: 30,
            background: theme.palette.headerColor || '#E8F1ED',
            transform: 'translate(0,-50%)',
            cursor: 'pointer',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '50%',
            height: 30,
            position: 'absolute',
            '@media only screen and (max-width: 600px) ': {
                display: 'none !important',
            },
            '& svg': {
                width: '0.65em',
                position: 'absolute',
                right: '18%',
            },
        },
        '& .slick-track': {
            display: 'flex',
        },
    },
    clubSelectionContainer: {
        display: 'flex',
        justifyContent: 'center',
        '@media only screen and (max-width: 600px) ': {
            marginTop: '15px',
        },
    },
});

const GraphqlClubSelectionV2 = ({
    classes,
    skuData,
    selectedSku,
    renderSkuPrice,
    skuMonths,
    changeClubMonth,
    changeClubLength,
    clubComponents,
    clubUpsells,
    changeClubUpsell,
    selectedClubUpsells,
    setShippingModalOpen,
    product,
}) => {
    const skuCollector = [];
    const dispatch = useDispatch();
    const monthTracker = (matchObject) => {
        dispatch(trackEvent({
            eventAction: 'club length',
            eventLabel: matchObject?.clubLength,
            eventCategory: 'Product Page',
        }),
        );
    };
    const seed = useUIDSeed();
    return (
        <div className={classes.mainContainer}>
            <h2 className={classes.header}>Build Your Club:</h2>
            <span className={classes.clubCopy}>Select how long you’d like your Club to last and when you’d like it to start. You will be charged only once. You can upgrade the first month of your Club to a special gift.</span>
            <span className={classes.link} aria-hidden="true" onClick={() => setShippingModalOpen(true)}>Shipping information & policies</span>
            <h3 className={classes.header}>1. Select a Club Length</h3>
            <div className={classes.clubSelectionContainer}>
                <Slider
                    dots={false}
                    speed={500}
                    infinite={false}
                    slidesToShow={4.5}
                    slidesToScroll={4}
                    nextArrow={<div><ArrowForwardIosIcon /></div>}
                    prevArrow={<div><ArrowBackIosIcon /></div>}
                    className={`carousel-container ${classes.carouselContainer}`}
                    responsive={[
                        {
                            breakpoint: 1024, // tablet breakpoint
                            settings: {
                                slidesToShow: 3.5,
                                slidesToScroll: 3,
                            },
                        },
                        {
                            breakpoint: 767, // mobile breakpoint
                            settings: {
                                slidesToShow: 2.75,
                                slidesToScroll: 2,
                            },
                        },
                    ]}
                >
                    {skuData?.map((sku) => {
                        if (!sku?.clubLength || !sku?.isSelectedMonth) {
                            return null;
                        }
                        if (((skuCollector || []).filter((skuList) => skuList?.length === sku?.clubLength).length)) {
                            return null;
                        }
                        if (selectedClubUpsells) {
                            skuCollector.push({
                                month: sku?.date?.month,
                                length: sku?.clubLength,
                                id: sku?.partNumber,
                            });
                        }
                        return (

                            <div
                                data-id={sku?.legacyId}
                                data-testid={`sku-list-${sku?.legacyId}`}
                                data-partnumber={sku?.partNumber}
                                data-selected-month={sku?.date?.month}
                                className={selectedSku.id === sku.id ? classes.clubSelectionBoxSelected : classes.clubSelectionBox}
                                key={seed(sku)}
                                onKeyPress={() => { }}
                                onClick={() => {
                                    changeClubLength((sku.partNumber || sku.id), sku.id);
                                    monthTracker(sku);
                                }}
                                tabIndex="0"
                                role="link"
                            >
                                {sku?.tagline && (
                                    <div className={classes.tagLineContainer}>
                                        <span className={classes.clubTagline}>{sku?.tagline}</span>
                                    </div>
                                )}
                                <p className={classes.clubPrice}>{renderSkuPrice(sku, true)}</p>
                                <p className={classes.clubDuration}>{sku?.clubLength} Months</p>
                            </div>

                        );
                    })}
                </Slider>
            </div>
            <h3 className={classes.header}>2. Starting Month</h3>
            <div className={classes.forMonthClub}>
                <GraphqlClubMonth
                    clubComponents={clubComponents}
                    itemsToShow={4.5}
                    itemsPerSlide={4}
                    changeClubMonth={changeClubMonth}
                    skuMonths={skuMonths}
                    selectedSku={selectedSku}
                    changeClubLength={changeClubLength}
                    selectedClubUpsells={selectedClubUpsells}
                    product={product}
                />
            </div>
            <div className={classes.monthContainer} />
            {clubUpsells.length > 0 && (
                <div className={classes.clubUpsellTitle}>
                    <p className={classes.header}>3. Optional Upgrade 1st Month</p>
                    <p className={classes.subHeader}>Swap your first month&apos;s delivery to a premium gift</p>
                </div>
            )}
            <GraphqlClubUpgrade
                clubUpsells={clubUpsells}
                selectedClubUpsells={selectedClubUpsells}
                changeClubUpsell={changeClubUpsell}
                clubComponents={clubComponents}
                itemsPerSlide={4}
                itemsToShow={4.5}
            />
        </div>
    );
};

GraphqlClubSelectionV2.defaultProps = {
    renderSkuPrice: () => { },
    skuMonths: {},
    clubUpsells: [],
    changeClubMonth: () => { },
    changeClubLength: () => { },
    changeClubUpsell: () => { },
    classes: {},
    selectedClubUpsells: null,
    product: {},
    setShippingModalOpen: false,
};

GraphqlClubSelectionV2.propTypes = {
    classes: object,
    renderSkuPrice: func,
    changeClubUpsell: func,
    changeClubMonth: func,
    skuData: skuDataType.isRequired,
    selectedSku: selectedSkuType.isRequired,
    skuMonths: object,
    changeClubLength: func,
    selectedClubUpsells: string,
    clubUpsells: UpsellType,
    clubComponents: object.isRequired,
    setShippingModalOpen: bool,
    product: object,
};

export default withStyles(styles)(GraphqlClubSelectionV2);
